import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar as CalendarIcon, Users, MapPin, Search } from 'lucide-react';
import { format } from 'date-fns';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';

const BookingSearch = ({ onSearch }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  
  const [checkInDate, setCheckInDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);
  const [guests, setGuests] = useState(2);
  const [destination, setDestination] = useState('all');
  const [showCheckInCal, setShowCheckInCal] = useState(false);
  const [showCheckOutCal, setShowCheckOutCal] = useState(false);
  const [showGuestsDropdown, setShowGuestsDropdown] = useState(false);

  const handleSearch = () => {
    if (!checkInDate || !checkOutDate) {
      return;
    }
    
    // Navigate to properties page with search params
    const params = new URLSearchParams({
      checkIn: format(checkInDate, 'yyyy-MM-dd'),
      checkOut: format(checkOutDate, 'yyyy-MM-dd'),
      guests: guests.toString(),
      destination: destination
    });
    
    navigate(`/properties?${params.toString()}`);
  };

  return (
    <div className="w-full max-w-5xl mx-auto">
      <div className="bg-white/95 backdrop-blur-sm rounded-sm border border-stone-200 shadow-xl p-3">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {/* Destination */}
          <div className="relative">
            <label className="text-xs font-medium text-stone-600 mb-1 block">
              {i18n.language === 'el' ? 'Προορισμός' : 'Destination'}
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <select
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                className="w-full pl-10 pr-3 py-3 border border-stone-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
              >
                <option value="all">{i18n.language === 'el' ? 'Όλα' : 'All'}</option>
                <option value="thessaloniki">{i18n.language === 'el' ? 'Θεσσαλονίκη' : 'Thessaloniki'}</option>
                <option value="halkidiki">{i18n.language === 'el' ? 'Χαλκιδική' : 'Halkidiki'}</option>
              </select>
            </div>
          </div>

          {/* Check-in */}
          <Popover open={showCheckInCal} onOpenChange={setShowCheckInCal}>
            <div>
              <label className="text-xs font-medium text-stone-600 mb-1 block">
                {i18n.language === 'el' ? 'Άφιξη' : 'Check-in'}
              </label>
              <PopoverTrigger asChild>
                <button className="w-full text-left pl-10 pr-3 py-3 border border-stone-200 rounded-sm text-sm hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary bg-white">
                  <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  {checkInDate ? format(checkInDate, 'dd/MM/yyyy') : (i18n.language === 'el' ? 'Επιλογή' : 'Select')}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={checkInDate}
                  onSelect={(date) => {
                    setCheckInDate(date);
                    setShowCheckInCal(false);
                  }}
                  disabled={(date) => date < new Date()}
                />
              </PopoverContent>
            </div>
          </Popover>

          {/* Check-out */}
          <Popover open={showCheckOutCal} onOpenChange={setShowCheckOutCal}>
            <div>
              <label className="text-xs font-medium text-stone-600 mb-1 block">
                {i18n.language === 'el' ? 'Αναχώρηση' : 'Check-out'}
              </label>
              <PopoverTrigger asChild>
                <button className="w-full text-left pl-10 pr-3 py-3 border border-stone-200 rounded-sm text-sm hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary bg-white">
                  <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  {checkOutDate ? format(checkOutDate, 'dd/MM/yyyy') : (i18n.language === 'el' ? 'Επιλογή' : 'Select')}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={checkOutDate}
                  onSelect={(date) => {
                    setCheckOutDate(date);
                    setShowCheckOutCal(false);
                  }}
                  disabled={(date) => !checkInDate || date <= checkInDate}
                />
              </PopoverContent>
            </div>
          </Popover>

          {/* Guests */}
          <Popover open={showGuestsDropdown} onOpenChange={setShowGuestsDropdown}>
            <div>
              <label className="text-xs font-medium text-stone-600 mb-1 block">
                {i18n.language === 'el' ? 'Επισκέπτες' : 'Guests'}
              </label>
              <PopoverTrigger asChild>
                <button className="w-full text-left pl-10 pr-3 py-3 border border-stone-200 rounded-sm text-sm hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary bg-white">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  {guests} {i18n.language === 'el' ? (guests === 1 ? 'Άτομο' : 'Άτομα') : (guests === 1 ? 'Guest' : 'Guests')}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-4" align="start">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => setGuests(Math.max(1, guests - 1))}
                      className="w-8 h-8 rounded-sm border border-stone-200 hover:bg-stone-100"
                    >
                      −
                    </button>
                    <span className="font-medium">{guests}</span>
                    <button
                      onClick={() => setGuests(Math.min(10, guests + 1))}
                      className="w-8 h-8 rounded-sm border border-stone-200 hover:bg-stone-100"
                    >
                      +
                    </button>
                  </div>
                  <button
                    onClick={() => setShowGuestsDropdown(false)}
                    className="w-full py-2 bg-primary text-white rounded-sm text-sm font-medium hover:bg-primary/90"
                  >
                    {i18n.language === 'el' ? 'Εντάξει' : 'Done'}
                  </button>
                </div>
              </PopoverContent>
            </div>
          </Popover>

          {/* Search Button */}
          <div className="flex items-end">
            <button
              onClick={handleSearch}
              disabled={!checkInDate || !checkOutDate}
              className="w-full py-3 bg-primary hover:bg-primary/90 text-white rounded-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              data-testid="search-properties-button"
            >
              <Search className="w-4 h-4" />
              <span className="text-sm">{i18n.language === 'el' ? 'Αναζήτηση' : 'Search'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSearch;
